import classNames from 'classnames';
import * as React from 'react';
import { Collapse } from 'react-collapse';

import { getTranslation, getTranslationWithFallback, keys } from '../../../../../../Resources/Translations';
import { errorAdditionalInfo } from '../../../../../ReactComponents/QuantityUtils';
import { RxMaintenanceModel } from '../../../../../Rx/RxMaintenanceModel.csharp';
import { AddToCartMessage } from './AddToCartMessage/AddToCartMessage';
import { RxMaintenanceAddToCartMessage } from './RxMaintenanceAddToCartMessage/RxMaintenanceAddToCartMessage';

interface AddToCartMessagesProps {
    errors: string[];
    warningMessage: string;
    maxQuantity: number;
    minQuantity: number;
    closeErrorMessage: () => void;
    isProductPage: boolean;
    isOnPrescription: boolean;
    rxMaintenance?: RxMaintenanceModel;
    isOutOfStock: boolean;
    isNotAvailable: boolean;
    showRefrigeratedProductWarning: boolean;
}

export const AddToCartMessages = (props: AddToCartMessagesProps) => {
    const getErrorMessage = () => {
        const { errors, closeErrorMessage, maxQuantity, minQuantity } = props;

        if (errors.length > 0) {
            return (
                <AddToCartMessage canBeClosed isError closeErrorMessage={closeErrorMessage} className="AddToCartMessage--ClosableError">
                    <div>
                        <span>{getTranslation(`addToCart.errorMessage`)}</span>
                        {errors.map((error) => (
                            <span key={error}>
                                {getTranslationWithFallback(`cartSummary.validation.${error}`, error)}
                                {errorAdditionalInfo(error, maxQuantity, minQuantity)}.
                            </span>
                        ))}
                    </div>
                </AddToCartMessage>
            );
        }
    };

    const getMessage = () => {
        const {
            isProductPage,
            warningMessage,
            closeErrorMessage,
            isOnPrescription,
            rxMaintenance,
            isNotAvailable,
            isOutOfStock,
            showRefrigeratedProductWarning
        } = props;

        if (isProductPage) {
            if (showRefrigeratedProductWarning) {
                return (
                    <>
                        <AddToCartMessage closeErrorMessage={closeErrorMessage} icon={<img src="/dist/assets/images/cold-delivery.svg" alt="icon" />}>
                            <span className="AddToCartMessage__ColdProductMessage">{getTranslation(keys['product.coldProductMessage'])}</span>
                        </AddToCartMessage>
                        {warningMessage && getWarningMessage()}
                    </>
                );
            }
            if (warningMessage) {
                return getWarningMessage();
            }
            if (isOutOfStock) {
                return (
                    <AddToCartMessage isError closeErrorMessage={closeErrorMessage}>
                        <span>{getTranslation(keys['product.outOfStockMessage'])}</span>
                    </AddToCartMessage>
                );
            }
            if (isNotAvailable) {
                return (
                    <AddToCartMessage isError closeErrorMessage={closeErrorMessage}>
                        <span>{getTranslation(keys['product.notAvailableMessage'])}</span>
                    </AddToCartMessage>
                );
            }
            if (isOnPrescription) {
                if (rxMaintenance?.showMessage) {
                    return <RxMaintenanceAddToCartMessage {...rxMaintenance} />;
                }

                return (
                    <AddToCartMessage closeErrorMessage={closeErrorMessage}>
                        <span>{getTranslation(keys['product.prescriptionMessage'])}</span>
                    </AddToCartMessage>
                );
            }
        }

        return false;
    };

    const getWarningMessage = () => {
        const { warningMessage, closeErrorMessage } = props;

        return (
            <AddToCartMessage closeErrorMessage={closeErrorMessage}>
                <span>{warningMessage}</span>
            </AddToCartMessage>
        );
    };

    const errorMessage = getErrorMessage();
    const message = getMessage();

    return (
        <div
            className={classNames(
                'AddToCart__Messages',
                props.isProductPage && 'AddToCart__Messages--WithAnimation',
                !!errorMessage && 'AddToCart__Messages--WithError',
                !!message && 'AddToCart__Messages--WithWarningMessage'
            )}
        >
            <Collapse isOpened>
                <div className="AddToCart__MessagesContent">
                    {errorMessage}
                    {message}
                </div>
            </Collapse>
        </div>
    );
};
